/**
 * THE mapping from an authored chart config to ``GenericChart`` props.
 *
 * One function, two hosts. The dashboard (``DashboardBody``) and the
 * Home board (``ChartTile``) both render the SAME ``GenericChart``, so
 * the config→props translation must exist exactly once. It used to be
 * written twice, and the copies drifted: the board's copy forwarded 18
 * props where the dashboard forwarded 26, silently dropping
 * ``meter_max``, ``card_columns``, ``segments``, ``thresholds``,
 * ``bands_legend``, ``legend_deltas``, ``info`` and ``subtitle``.
 *
 * What that cost, measured on staging 2026-08-04: a scorecard whose
 * data was ``0.9704`` rendered as **"1.0"** instead of **"97.0%"**
 * (``meter_max`` gone), four country cards squeezed into a hardcoded
 * 4-column grid until the names read "U." / "C." / "M."
 * (``card_columns`` gone), and stacked bars with no legend for their
 * colours (``segments`` gone) — on a product whose owner is red-green
 * colourblind and depends on that legend.
 *
 * A number that renders wrong is not a styling bug, it is a wrong
 * number. So this is not a refactor for tidiness: a second copy of this
 * mapping is a latent data-integrity defect, and the only durable fix
 * is that there is nowhere for a second copy to live.
 *
 * Localisation is injected rather than imported so this module stays a
 * pure function of (config, lang): the caller passes the same
 * ``resolveLocalized`` both hosts already use.
 */

import type { ChartConfig, DisplayLabel } from "./types";

/** Props derived from the CONFIG alone. Everything positional (data,
 *  error, cellPx) and everything host-specific (cross-filter wiring,
 *  swap state) stays with the caller. */
export interface ChartConfigProps {
  info?: string;
  subtitle?: string;
  subtitleInline: boolean;
  density?: string;
  showShare: boolean;
  legendDeltas: boolean;
  tooltipTotal: boolean;
  thresholds?: ChartConfig["thresholds"];
  bandsLegend: boolean;
  segments?: ChartConfig["segments"];
  cardColumns?: number;
  meterMax?: number;
  tone: ChartConfig["tone"];
  tones: ChartConfig["tones"];
  stacked: boolean;
  xFormat: unknown;
  yFormat: unknown;
  dataLabels: unknown;
  axisXFormat: unknown;
  axisYFormat: unknown;
  axisY1Format: unknown;
  dualAxis: unknown;
  tableColumns: unknown;
  mapVariant: unknown;
  calendarYear: unknown;
  timelineMonths: unknown;
  rankMarker?: string;
  legendToggle: boolean;
  emptyMessage?: string;
}

const EMPTY_TONES: NonNullable<ChartConfig["tones"]> = [];
const EMPTY_TABLE_COLUMNS: unknown[] = [];

export interface ChartPropsOptions {
  /** Dashboard-level bands a block inherits when it authors none. */
  dashboardThresholds?: ChartConfig["thresholds"];
  /** The host's ``resolveLocalized`` — injected, never imported here. */
  resolveLabel: (label: DisplayLabel | undefined | null) => string;
}

export function chartPropsFromConfig(
  cfg: ChartConfig,
  opts: ChartPropsOptions,
): ChartConfigProps {
  const { dashboardThresholds, resolveLabel } = opts;
  return {
    info: resolveLabel(cfg.info as DisplayLabel) || undefined,
    subtitle: resolveLabel(cfg.subtitle as DisplayLabel) || undefined,
    // Authored default is ON — only an explicit `false` turns it off.
    legendDeltas: cfg.legend_deltas !== false,
    tooltipTotal: cfg.tooltip_total !== false,
    // Epic v3 scorecards — bands are authored per chart, else the block
    // INHERITS the dashboard-level bands (PR-A1). MUST be length-gated,
    // not `?? dashboardThresholds`: the engine emits an absent per-block
    // value as `[]` (empty array, not null), so nullish-coalescing would
    // never fall back.
    thresholds: cfg.thresholds?.length ? cfg.thresholds : dashboardThresholds,
    subtitleInline: cfg.subtitle_inline === true,
    density: cfg.density ?? undefined,
    // Ausente = ON: los rankeados de conteos que ya existen lo
    // esperan, y solo el autor sabe si su magnitud es aditiva.
    showShare: cfg.show_share !== false,
    bandsLegend: cfg.bands_legend === true,
    segments: cfg.segments,
    cardColumns: cfg.card_columns ?? undefined,
    meterMax: cfg.meter_max ?? undefined,
    tone: cfg.tone ?? null,
    tones: cfg.tones ?? EMPTY_TONES,
    stacked: cfg.stacked ?? false,
    xFormat: cfg.x_format ?? null,
    yFormat: cfg.y_format ?? null,
    dataLabels: cfg.data_labels ?? null,
    axisXFormat: cfg.axis_format?.x ?? null,
    axisYFormat: cfg.axis_format?.y ?? null,
    axisY1Format: cfg.axis_format?.y1 ?? null,
    dualAxis: cfg.dual_axis ?? false,
    tableColumns: cfg.table_columns ?? EMPTY_TABLE_COLUMNS,
    mapVariant: cfg.map_variant ?? undefined,
    calendarYear: cfg.year ?? null,
    timelineMonths: cfg.months_csv ?? null,
    rankMarker: cfg.rank_marker ?? undefined,
    legendToggle: cfg.legend_toggle === true,
    emptyMessage: resolveLabel(cfg.empty_message as DisplayLabel) || undefined,
  };
}
